import React, { useEffect, useState } from 'react';
import {
  ActionIcon, Alert, Badge, Button, Card, Group, List, Modal, Select, Stack, Text, TextInput,
  Textarea, Title, Tooltip,
} from '@mantine/core';
import {
  IconAlertCircle, IconCheck, IconCoin, IconDownload, IconFileText, IconRefresh, IconSitemap,
  IconSparkles, IconTrash,
} from '@tabler/icons-react';

import { submitGeneration, getArtifactUrl, saveFlowPlan } from '../api_scripts/solnStudioClient';
import { FLOW_TYPE_SELECT_DATA, FLOW_TYPE_LABELS, normalizePlanEntries } from '../flowCatalog';
import { formatUsd, hasDesignContent } from '../utils/format';
import useJobPoll from '../hooks/useJobPoll';
import ActionHelp from '../components/ActionHelp';
import FlowGradeBadge from '../components/FlowGradeBadge';
import JobStatusAlert from '../components/JobStatusAlert';
import PanelStub from '../components/PanelStub';

// Diagrams tab — the explicit "Generate flows" step. Flows (the §6B pipeline) are NO LONGER part
// of a Design Refine; they're generated here, deliberately, once the design artifacts + open questions
// are settled. Regeneration overwrites hand-edited flows, so we warn-and-confirm first; open questions
// are an advisory (non-blocking) nudge.
export default function DiagramsPanel({ project, designModel, getApiToken, onChanged, locked, onEdit }) {
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [costGateOpen, setCostGateOpen] = useState(false); // cost-gate modal for full flows runs
  const [costGateEstimate, setCostGateEstimate] = useState(null); // { costUsd, durationMs } from estimate
  // Targeted single-flow regenerate: the flow being regenerated + the engineer's optional feedback.
  const [regen, setRegen] = useState(null); // { flow } while the feedback modal is open
  const [regenFeedback, setRegenFeedback] = useState('');
  // Plan-approval gate: the proposed flow inventory awaiting the engineer's OK before Opus builders run.
  // Persisted as the FLOW_PLAN artifact (survives reload) — loaded below; also set from a plan-only job.
  const [reviewPlan, setReviewPlan] = useState(null); // [{ name, type, flowType, responsibility, branches, invokes }]
  const [planVersion, setPlanVersion] = useState(null); // designModelVersion the loaded plan was built against
  const [planEditing, setPlanEditing] = useState(false);
  const [planDraft, setPlanDraft] = useState(null); // editable copy while planEditing
  const [planBusy, setPlanBusy] = useState(false);
  const [pendingOpts, setPendingOpts] = useState(null); // opts held while the replace-confirm modal is open

  // Shared job loop (P0.4): resumes an in-flight flows build on mount (tab switch / reload no longer
  // lose a minutes-long Opus build) and keeps the terminal result rendered until dismissed.
  const { job, stalled, running, start, dismiss } = useJobPoll({
    project,
    getApiToken,
    match: (j) => j.kind === 'design' && j.stage === 'flows',
    onTerminal: (j) => {
      // A plan-only run surfaces its inventory inline for review; a build refreshes the flow list.
      const entries = j.status === 'SUCCEEDED'
        ? (j.result?.flowPlan?.entries || j.result?.summary?.flowPlan?.entries)
        : null;
      if (entries) setReviewPlan(normalizePlanEntries(entries));
      onChanged();
    },
  });

  // Load the persisted FLOW_PLAN artifact so the proposed-plan card survives a reload / tab switch.
  const flowPlanArtifact = (project.artifacts || []).find((a) => a.type === 'FLOW_PLAN') || null;
  const flowPlanArtifactKey = flowPlanArtifact ? `${flowPlanArtifact.s3Key}|${flowPlanArtifact.generatedAt}` : null;
  useEffect(() => {
    let cancelled = false;
    if (!flowPlanArtifact) { setReviewPlan(null); setPlanVersion(null); return undefined; }
    (async () => {
      try {
        const { url } = await getArtifactUrl(project.projectId, flowPlanArtifact.id, getApiToken);
        const res = await fetch(url);
        if (!res.ok) throw new Error(`Failed to read flow plan (${res.status})`);
        const stored = await res.json();
        if (!cancelled && Array.isArray(stored.plan)) {
          setReviewPlan(normalizePlanEntries(stored.plan));
          setPlanVersion(flowPlanArtifact.version ?? null);
        }
      } catch { /* leave the inline/job-sourced plan in place on a fetch error */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowPlanArtifactKey]);

  const flows = designModel?.flows || [];
  // A model worth generating flows from must have some design content (intents/queues/prompts/…).
  const hasModel = hasDesignContent(designModel);
  const openQuestions = (designModel?.openQuestions || []).filter((q) => q.status !== 'resolved').length;
  // Flows whose DSL a human (or brownfield reverse-derivation) owns — regeneration would replace them.
  const editedFlows = flows.filter((f) => f.source === 'human' || f.source === 'instance-export');

  // Submit a flows-stage job. `opts` selects the variant:
  //   {}                     → full Architect→Builders→Verify→Judge pass over every flow
  //   { planOnly: true }     → run ONLY the architect + persist the plan for review (no Opus builders)
  //   { usePlan: true }      → build from the already-approved plan (skips the architect)
  //   { targetFlow, feedback } → targeted single-flow regenerate
  const doGenerate = async (opts = {}) => {
    setConfirmOpen(false);
    setCostGateOpen(false);
    setPendingOpts(null);
    setRegen(null);
    if (opts.usePlan || (!opts.planOnly && !opts.targetFlow)) setReviewPlan(null); // a build clears the pending plan
    setBusy(true); setError(null);
    try {
      const res = await submitGeneration(project.projectId, { kind: 'design', mode: 'fast', stage: 'flows', ...opts }, getApiToken);
      start(res.jobId, {
        jobId: res.jobId, status: res.status,
        estimatedCostUsd: res.estimate?.costUsd, estimatedDurationMs: res.estimate?.durationMs,
      });
    } catch (err) {
      setError(err.reason === 'locked' ? 'A generation job is already running on this project.' : err.message);
    } finally { setBusy(false); }
  };

  // A build that would replace hand-edited/brownfield flows warns first; a full flows run shows
  // the cost estimate first. Both modals can combine (replace-confirm + cost-gate).
  const requestGenerate = (opts = {}) => {
    // Full flows runs (not planOnly, not usePlan, not targeted single-flow) require cost confirm.
    const isFullRun = !opts.planOnly && !opts.usePlan && !opts.targetFlow;
    if (isFullRun) {
      // Full run needs cost gate; combine with replace-confirm if there are edited flows.
      setCostGateOpen(true);
      setCostGateEstimate(null); // will be fetched/shown in the modal
      if (editedFlows.length > 0) setPendingOpts(opts);
      return;
    }
    // For planOnly/usePlan: only warn if replacing hand-edited flows.
    if (editedFlows.length > 0) { setPendingOpts(opts); setConfirmOpen(true); }
    else doGenerate(opts);
  };
  // Rebuild ONE flow against its stored plan + the engineer's feedback (leaves the others untouched).
  const submitRegen = () => doGenerate({ targetFlow: regen.flow.name, feedback: regenFeedback.trim() || undefined });

  // ---- Flow-plan review/edit (persistent FLOW_PLAN artifact) ----
  const planStale = planVersion != null && project.designModelVersion != null && planVersion !== project.designModelVersion;
  const downloadPlan = async () => {
    if (!flowPlanArtifact) return;
    try { const { url } = await getArtifactUrl(project.projectId, flowPlanArtifact.id, getApiToken); window.open(url, '_blank'); }
    catch (e) { setError(e.message); }
  };
  const startEditPlan = () => { setPlanDraft((reviewPlan || []).map((e) => ({ ...e }))); setPlanEditing(true); };
  const cancelEditPlan = () => { setPlanEditing(false); setPlanDraft(null); };
  const updateDraft = (i, patch) => setPlanDraft((d) => {
    // When an entry is renamed, rewrite any OTHER entry's invokes/transfersTo that referenced the old
    // name so the plan stays internally consistent (the EventHooks customer-queue ref is handled by the
    // backend flow directory; this keeps module/flow composition refs aligned with the rename).
    const oldName = d[i] && d[i].name;
    const renamed = Object.prototype.hasOwnProperty.call(patch, 'name') && patch.name && oldName && patch.name !== oldName;
    return d.map((e, idx) => {
      if (idx === i) return { ...e, ...patch };
      if (!renamed) return e;
      const rewrite = (arr) => (Array.isArray(arr) && arr.includes(oldName) ? arr.map((n) => (n === oldName ? patch.name : n)) : arr);
      const invokes = rewrite(e.invokes);
      const transfersTo = rewrite(e.transfersTo);
      return invokes === e.invokes && transfersTo === e.transfersTo ? e : { ...e, invokes, transfersTo };
    });
  });
  const removeDraftEntry = (i) => setPlanDraft((d) => d.filter((_, idx) => idx !== i));
  const savePlan = async () => {
    // Normalize before persisting so an entry whose flowType was changed in the editor keeps its
    // coarse `type`/`isModule` consistent with it (flowType is authoritative) — the backend builder
    // reads flowType, but a stale `type` would mislead any type-based consumer.
    const cleaned = normalizePlanEntries(
      (planDraft || []).map((e) => ({ ...e, name: (e.name || '').trim() })).filter((e) => e.name)
    );
    if (cleaned.length === 0) { setError('A plan needs at least one named flow.'); return; }
    setPlanBusy(true); setError(null);
    try {
      const { plan: saved } = await saveFlowPlan(project.projectId, project.version, cleaned, getApiToken);
      setReviewPlan(normalizePlanEntries(saved));
      setPlanEditing(false);
      setPlanDraft(null);
      onChanged();
    } catch (err) {
      setError(err.reason === 'locked' ? 'A generation job is running — try again when it finishes.'
        : err.reason === 'stale-version' ? 'This project changed — reopen it and retry.' : err.message);
    } finally { setPlanBusy(false); }
  };

  if (!hasModel) {
    return <PanelStub title="Contact-flow & conceptual diagrams" step="Step 3"
      blurb="Flow-DSL → React Flow viewer + Lucid export + the in-app interactive editor (§8). Generate the Design Model first (Design tab), then generate the contact flows here — keeping the expensive flow pass out of routine design iteration." />;
  }

  return (
    <Stack>
      <Card withBorder padding="lg" radius="md">
        <Group justify="space-between" align="flex-start" mb="sm">
          <div>
            <Group gap="xs" align="center">
              <Title order={4}>Contact flows</Title>
              <Tooltip label="Flows are generated by the Opus flow pass — the dominant cost of a design. It's separate from Design Refine so you only run it once the design is settled." withArrow multiline w={280}>
                <Badge variant="light" color="gray" leftSection={<IconCoin size={12} />}>
                  AI cost: {formatUsd(project.costByPhase?.design ?? 0)} total (design)
                </Badge>
              </Tooltip>
            </Group>
            <Text size="sm" c="dimmed">
              {flows.length > 0
                ? `${flows.length} flow(s). Open one to edit in the interactive editor — click Save there to persist your changes.`
                : 'No flows yet. Generate them from the settled design — they reference the intents and queues already in the model.'}
            </Text>
          </div>
          <Group gap="xs">
            <Tooltip
              label={<ActionHelp
                reads="the settled Design Model"
                does="runs only the architect to propose the flow/module inventory for your review BEFORE any (Opus) build cost"
                updates="saves a FLOW_PLAN for review — no build cost, no new Design Model version. Then build from the approved plan."
              />}
              withArrow multiline w={300}
            >
              <Button
                variant="light" color="orange"
                leftSection={<IconSitemap size={16} />}
                loading={busy || running}
                disabled={locked || busy || running}
                onClick={() => doGenerate({ planOnly: true })}
              >
                Plan flows
              </Button>
            </Tooltip>
            <Tooltip
              label={flows.length > 0
                ? <ActionHelp
                    reads="the current Design Model + your existing flows"
                    does="re-runs the full flow pipeline (Architect → per-flow Builders → Verify → Judge) against the latest design"
                    updates="replaces generated flows (and removes generated flows the new plan drops); hand-edited / brownfield flows are replaced only after you confirm. To rebuild just one flow, use its per-flow Regenerate."
                  />
                : <ActionHelp
                    reads="the settled Design Model — intents, queues, prompts, integrations"
                    does="Architect → per-flow Builders → Verify → Judge (the Opus flow pass — the dominant cost of a design)"
                    updates="creates the contact flows + the FLOW_PLAN; does not touch the Design Model"
                  />}
              withArrow multiline w={320}
            >
              <Button
                color="orange"
                leftSection={<IconSparkles size={16} />}
                loading={busy || running}
                disabled={locked || busy || running}
                onClick={() => requestGenerate({})}
              >
                {flows.length > 0 ? 'Refine flows' : 'Generate flows'}
              </Button>
            </Tooltip>
          </Group>
        </Group>

        {openQuestions > 0 && (
          <Alert color="yellow" mt="xs" icon={<IconAlertCircle size={16} />}>
            {openQuestions} open question{openQuestions === 1 ? '' : 's'} unresolved — these may affect routing.
            Resolve them on the Design tab for the best flows, or generate anyway.
          </Alert>
        )}
        {error && <Alert color="red" mt="sm" icon={<IconAlertCircle size={16} />}>{error}</Alert>}

        <JobStatusAlert job={job} stalled={stalled} title="Flow generation" onDismiss={dismiss} />
      </Card>

      {reviewPlan && reviewPlan.length > 0 && (
        <Card withBorder padding="lg" radius="md" style={{ borderColor: 'var(--mantine-color-orange-4)' }}>
          <Group justify="space-between" align="flex-start" mb="sm">
            <div>
              <Group gap="xs" align="center">
                <IconSitemap size={18} />
                <Title order={5}>Proposed flow plan{planEditing ? ' — editing' : ' — review before building'}</Title>
                {planStale && !planEditing && (
                  <Tooltip label="The design changed after this plan was made — re-run Plan flows to refresh it." withArrow>
                    <Badge size="xs" color="yellow" variant="light">out of date</Badge>
                  </Tooltip>
                )}
              </Group>
              <Text size="sm" c="dimmed">
                {(planEditing ? planDraft : reviewPlan).length} flow(s) planned. Building runs one Opus
                builder per flow — review (and edit) the inventory and types first, then build from this
                plan (the architect step is skipped).
              </Text>
            </div>
            {!planEditing ? (
              <Group gap="xs">
                <Button size="sm" variant="subtle" color="gray" leftSection={<IconDownload size={14} />}
                  disabled={!flowPlanArtifact} onClick={downloadPlan}>Download</Button>
                <Button size="sm" variant="light" color="orange" leftSection={<IconFileText size={14} />}
                  disabled={locked || busy || running} onClick={startEditPlan}>Edit plan</Button>
                <Tooltip
                  label={<ActionHelp
                    reads="the approved FLOW_PLAN above"
                    does="runs one Opus builder per flow, skipping the architect step"
                    updates="creates the contact flows from the reviewed plan"
                  />}
                  withArrow multiline w={300}
                >
                  <Button
                    size="sm" color="orange" leftSection={<IconCheck size={16} />} loading={busy || running}
                    disabled={locked || busy || running} onClick={() => requestGenerate({ usePlan: true })}
                  >
                    Build flows from plan
                  </Button>
                </Tooltip>
              </Group>
            ) : (
              <Group gap="xs">
                <Button size="sm" variant="subtle" color="gray" disabled={planBusy} onClick={cancelEditPlan}>Cancel</Button>
                <Button size="sm" color="orange" leftSection={<IconCheck size={16} />} loading={planBusy}
                  onClick={savePlan}>Save plan</Button>
              </Group>
            )}
          </Group>

          {!planEditing ? (
            <List size="sm" spacing={4}>
              {reviewPlan.map((e, i) => (
                <List.Item
                  key={i}
                  icon={<Badge size="xs" variant="light" color={e.isModule ? 'grape' : 'orange'}>{FLOW_TYPE_LABELS[e.flowType] || e.flowType}</Badge>}
                >
                  <Text span fw={500}>{e.name}</Text>
                  {e.responsibility ? <Text span c="dimmed"> — {e.responsibility}</Text> : null}
                  {(e.invokes || []).length > 0 && <Text span c="dimmed" size="xs"> · invokes: {e.invokes.join(', ')}</Text>}
                </List.Item>
              ))}
            </List>
          ) : (
            <Stack gap="xs">
              {(planDraft || []).map((e, i) => (
                <Group key={i} gap="xs" align="flex-start" wrap="nowrap">
                  <TextInput size="xs" style={{ width: 220 }} placeholder="Flow name" value={e.name || ''}
                    onChange={(ev) => updateDraft(i, { name: ev.currentTarget.value })} />
                  <Select size="xs" style={{ width: 200 }} data={FLOW_TYPE_SELECT_DATA}
                    value={e.flowType || 'CONTACT_FLOW'} onChange={(v) => updateDraft(i, { flowType: v })} />
                  <TextInput size="xs" style={{ flex: 1 }} placeholder="Responsibility (optional)"
                    value={e.responsibility || ''} onChange={(ev) => updateDraft(i, { responsibility: ev.currentTarget.value })} />
                  <ActionIcon size="sm" variant="subtle" color="red" onClick={() => removeDraftEntry(i)} title="Remove">
                    <IconTrash size={14} />
                  </ActionIcon>
                </Group>
              ))}
              <Text size="xs" c="dimmed">
                Renaming, re-typing, or removing entries here changes only the plan — build from it when ready.
              </Text>
            </Stack>
          )}
        </Card>
      )}

      {flows.map((f) => (
        <Card key={f.id} withBorder padding="md" radius="md">
          <Group justify="space-between">
            <Group gap="xs">
              <Text fw={500}>{f.name}</Text>
              <Badge size="xs" variant="light" color={f.type === 'module' ? 'grape' : 'orange'}>{f.type}</Badge>
              {f.source === 'instance-export' && <Badge size="xs" variant="outline" color="grape">brownfield</Badge>}
              {f.source === 'human' && <Badge size="xs" variant="outline" color="teal">edited</Badge>}
              <Badge size="xs" variant="outline" color="gray">{(f.dsl?.nodes || []).length} nodes</Badge>
              {f.buildValid === false && (
                <Tooltip label={(f.buildErrors || []).join('; ') || 'Flow graph did not validate'} withArrow multiline w={300}>
                  <Badge size="xs" variant="light" color="red" leftSection={<IconAlertCircle size={11} />}>invalid graph</Badge>
                </Tooltip>
              )}
              {(f.buildWarnings || []).length > 0 && (
                <Tooltip label={f.buildWarnings.join('; ')} withArrow multiline w={300}>
                  <Badge size="xs" variant="light" color="yellow" leftSection={<IconAlertCircle size={11} />}>
                    {f.buildWarnings.length} cross-link
                  </Badge>
                </Tooltip>
              )}
              <FlowGradeBadge grade={f.qualityGrade} />
            </Group>
            <Group gap="xs">
              {f.source !== 'human' && (
                <Tooltip label="Rebuild just this flow against its plan + your feedback" withArrow>
                  <Button size="xs" variant="subtle" color="grape" leftSection={<IconRefresh size={14} />}
                    disabled={locked || busy || running}
                    onClick={() => { setRegenFeedback(''); setRegen({ flow: f }); }}>
                    Regenerate
                  </Button>
                </Tooltip>
              )}
              <Button size="xs" variant="light" color="orange" onClick={() => onEdit(f.id)}>Edit</Button>
            </Group>
          </Group>
        </Card>
      ))}

      <Modal opened={!!regen} onClose={() => setRegen(null)} title={`Regenerate "${regen?.flow?.name || ''}"`} centered>
        <Stack>
          <Text size="sm" c="dimmed">
            Rebuilds only this flow from its architect plan, leaving the others untouched. Add feedback to
            steer the rebuild (e.g. a judge finding to address, or a branch to add).
          </Text>
          {(regen?.flow?.qualityGrade?.findings || []).length > 0 && (
            <Alert color="yellow" variant="light" icon={<IconAlertCircle size={14} />}>
              <Text size="xs" fw={600} mb={4}>Judge findings:</Text>
              <List size="xs">
                {regen.flow.qualityGrade.findings.slice(0, 6).map((fd, i) => (
                  <List.Item key={i}>{fd.issue}{fd.fix ? ` — ${fd.fix}` : ''}</List.Item>
                ))}
              </List>
            </Alert>
          )}
          <Textarea
            label="Feedback (optional)" autosize minRows={2} maxRows={6}
            placeholder="What should change about this flow?"
            value={regenFeedback} onChange={(e) => setRegenFeedback(e.currentTarget.value)}
          />
          <Group justify="flex-end" gap="xs">
            <Button variant="subtle" color="gray" onClick={() => setRegen(null)}>Cancel</Button>
            <Button color="grape" leftSection={<IconRefresh size={16} />} loading={busy} onClick={submitRegen}>Regenerate flow</Button>
          </Group>
        </Stack>
      </Modal>

      <Modal opened={confirmOpen} onClose={() => { setConfirmOpen(false); setPendingOpts(null); }} title="Regenerate flows?" centered>
        <Stack>
          <Text size="sm">
            {editedFlows.length} flow{editedFlows.length === 1 ? '' : 's'} {editedFlows.length === 1 ? 'has' : 'have'} manual or
            brownfield edits that {pendingOpts?.usePlan ? 'building from the plan' : 'regeneration'} will <b>replace</b>:
          </Text>
          <List size="sm">
            {editedFlows.slice(0, 12).map((f) => (
              <List.Item key={f.id}>{f.name} <Text span c="dimmed" size="xs">({f.source === 'human' ? 'edited' : 'brownfield'})</Text></List.Item>
            ))}
            {editedFlows.length > 12 && <List.Item>…and {editedFlows.length - 12} more</List.Item>}
          </List>
          <Group justify="flex-end" gap="xs">
            <Button variant="subtle" color="gray" onClick={() => { setConfirmOpen(false); setPendingOpts(null); }}>Cancel</Button>
            <Button color="orange" onClick={() => doGenerate(pendingOpts || {})}>Regenerate &amp; replace</Button>
          </Group>
        </Stack>
      </Modal>

      <Modal opened={costGateOpen} onClose={() => { setCostGateOpen(false); setPendingOpts(null); }} title="Confirm flow generation cost" centered size="sm">
        <Stack>
          <Text size="sm">
            This generates flows for all branches in your design. It's the most expensive step (Opus architect + per-flow builders).
          </Text>
          <Card withBorder padding="md" radius="md" bg="orange.0">
            <Group justify="space-between" align="center">
              <Stack gap={0}>
                <Text size="xs" c="dimmed">Estimated cost</Text>
                <Text size="lg" fw={600}>{formatUsd(project.costByPhase?.design ?? 0)} total (design phase)</Text>
              </Stack>
              <Stack gap={0}>
                <Text size="xs" c="dimmed">Est. duration</Text>
                <Text size="sm" fw={500}>{project.costByPhase?.design ? '~10-15 min' : '5-10 min'}</Text>
              </Stack>
            </Group>
          </Card>
          {editedFlows.length > 0 && (
            <Alert color="yellow" icon={<IconAlertCircle size={16} />}>
              This will replace {editedFlows.length} hand-edited/brownfield flow{editedFlows.length === 1 ? '' : 's'}.
            </Alert>
          )}
          <Group justify="flex-end" gap="xs">
            <Button variant="subtle" color="gray" onClick={() => { setCostGateOpen(false); setPendingOpts(null); }}>Cancel</Button>
            <Button
              color="orange"
              onClick={() => {
                if (editedFlows.length > 0) { setConfirmOpen(true); }
                else doGenerate(pendingOpts || {});
              }}
            >
              Generate flows
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
